// src/hooks/useFirestore.js
import { useState, useCallback } from 'react';
import {
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  doc,
  collection,
  serverTimestamp,
  writeBatch,
  runTransaction
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';

/**
 * Función auxiliar para eliminar valores undefined de un objeto
 * Firestore NO acepta undefined, solo acepta null o valores concretos
 */
const removeUndefinedValues = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined) {
      // Si es undefined, lo convertimos a null (Firestore sí acepta null)
      cleaned[key] = null;
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
      // Si es un objeto anidado, limpiamos recursivamente
      cleaned[key] = removeUndefinedValues(value);
    } else {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

export const useFirestore = (collectionName) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { userProfile } = useAuth();

  // Crear documento con ID automático
  const add = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // LIMPIAMOS los datos antes de enviarlos
      const cleanedData = removeUndefinedValues(data);
      
      const enrichedData = {
        ...cleanedData,
        createdBy: userProfile?.uid || 'system',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: cleanedData.isActive !== undefined ? cleanedData.isActive : true
      };

      const docRef = await addDoc(collection(db, collectionName), enrichedData);
      
      setSuccess(true);
      // Retornamos con fecha legible para uso local
      return { 
        id: docRef.id, 
        ...cleanedData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    } catch (err) {
      console.error(`Error adding document to ${collectionName}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [collectionName, userProfile]);

  // Crear documento con ID personalizado
  const set = useCallback(async (docId, data, merge = false) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const cleanedData = removeUndefinedValues(data);
      const docRef = doc(db, collectionName, docId);
      const enrichedData = {
        ...cleanedData,
        updatedBy: userProfile?.uid || 'system',
        updatedAt: serverTimestamp()
      };

      if (!merge) {
        enrichedData.createdBy = userProfile?.uid || 'system';
        enrichedData.createdAt = serverTimestamp();
      }

      await setDoc(docRef, enrichedData, { merge });
      
      setSuccess(true);
      return { id: docId, ...enrichedData };
    } catch (err) {
      console.error(`Error setting document in ${collectionName}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [collectionName, userProfile]);

  // Actualizar documento
  const update = useCallback(async (docId, data) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const cleanedData = removeUndefinedValues(data);
      const docRef = doc(db, collectionName, docId);
      const updateData = {
        ...cleanedData,
        updatedBy: userProfile?.uid || 'system',
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updateData);
      
      setSuccess(true);
      return { id: docId, ...updateData };
    } catch (err) {
      console.error(`Error updating document in ${collectionName}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [collectionName, userProfile]);

  // Soft delete (marcar como inactivo)
  const softDelete = useCallback(async (docId) => {
    return update(docId, {
      isActive: false,
      deletedAt: serverTimestamp(),
      deletedBy: userProfile?.uid || 'system'
    });
  }, [update, userProfile]);

  // Hard delete (eliminar permanentemente)
  const remove = useCallback(async (docId) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const docRef = doc(db, collectionName, docId);
      await deleteDoc(docRef);
      
      setSuccess(true);
      return docId;
    } catch (err) {
      console.error(`Error deleting document from ${collectionName}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [collectionName]);

  // Operación batch (múltiples escrituras atómicas)
  const batch = useCallback(async (operations) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const batchWrite = writeBatch(db);
      const results = [];

      operations.forEach(op => {
        const docRef = doc(db, collectionName, op.id);
        const cleanedData = removeUndefinedValues(op.data);
        const data = {
          ...cleanedData,
          updatedBy: userProfile?.uid || 'system',
          updatedAt: serverTimestamp()
        };

        if (op.type === 'set') {
          batchWrite.set(docRef, data, { merge: op.merge || false });
        } else if (op.type === 'update') {
          batchWrite.update(docRef, data);
        } else if (op.type === 'delete') {
          batchWrite.delete(docRef);
        }
        results.push({ id: op.id, type: op.type });
      });

      await batchWrite.commit();
      
      setSuccess(true);
      return results;
    } catch (err) {
      console.error(`Error in batch operation for ${collectionName}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [collectionName, userProfile]);

  // Transacción (lectura-escritura atómica)
  const transaction = useCallback(async (updateFn) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await runTransaction(db, async (transaction) => {
        return await updateFn(transaction, collection(db, collectionName));
      });
      
      setSuccess(true);
      return result;
    } catch (err) {
      console.error(`Error in transaction for ${collectionName}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [collectionName]);

  // Restaurar documento
  const restore = useCallback(async (docId) => {
    return update(docId, {
      isActive: true,
      restoredAt: serverTimestamp(),
      restoredBy: userProfile?.uid || 'system',
      deletedAt: null,
      deletedBy: null
    });
  }, [update, userProfile]);

  // Duplicar documento
  const duplicate = useCallback(async (docId) => {
    setLoading(true);
    setError(null);

    try {
      const { getDoc } = await import('firebase/firestore');
      const originalDoc = await getDoc(doc(db, collectionName, docId));
      
      if (!originalDoc.exists()) {
        throw new Error('Documento original no encontrado');
      }

      const originalData = originalDoc.data();
      const { id, createdBy, createdAt, updatedAt, updatedBy, ...dataToCopy } = originalData;

      const newDoc = await add({
        ...dataToCopy,
        name: `${originalData.name || ''} (copia)`,
        status: 'draft'
      });

      return newDoc;
    } catch (err) {
      console.error(`Error duplicating document in ${collectionName}:`, err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [collectionName, add]);

  // Resetear estado
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    add,
    set,
    update,
    remove,
    softDelete,
    restore,
    batch,
    transaction,
    duplicate,
    loading,
    error,
    success,
    reset
  };
};

export default useFirestore;